import { ReportDateRangePicker } from "../../Sales/js/ReportDateRangePicker";
import { duration } from "./FrontDesk";
import { whiteBadgeFill } from "./api";
import { WorkloadPivot } from "./WorkloadPivot";
import React, { useEffect, useState, useRef } from "react";
import {
  HistoryEvent,
  request,
  Room,
  Schedule,
  shiftDate,
  displayInstant,
  displayTime,
} from "./api";
export function History({
  from,
  rooms,
  timezone,
}: {
  from: string;
  rooms: Room[];
  timezone: string;
}) {
  const [historyFrom, setHistoryFrom] = useState(shiftDate(from, -13)),
    [historyTo, setHistoryTo] = useState(from),
    [selectedRange, setSelectedRange] = useState(""),
    [roomId, setRoomId] = useState("");
  const [items, setItems] = useState<HistoryEvent[]>([]),
    [cursor, setCursor] = useState<string | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);
  async function load(next?: string) {
    const version = ++requestVersion.current;
    setError("");
    if (!next) {
      setItems([]);
      setCursor(null);
    }
    const span =
      (Date.parse(historyTo) - Date.parse(historyFrom)) / 86400000 + 1;
    if (
      !historyFrom ||
      !historyTo ||
      !Number.isFinite(span) ||
      span < 1 ||
      span > 366
    ) {
      setError("Choose a start and end date covering no more than 366 days.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await request<{
        items: HistoryEvent[];
        nextCursor: string | null;
      }>(
        `/history?from=${historyFrom}&to=${historyTo}&limit=50${roomId ? `&roomId=${encodeURIComponent(roomId)}` : ""}${next ? `&cursor=${encodeURIComponent(next)}` : ""}`,
      );
      if (version !== requestVersion.current) return;
      setItems((old) => (next ? [...old, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch (e) {
      if (version === requestVersion.current) setError((e as Error).message);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    return () => {
      requestVersion.current++;
    };
  }, [historyFrom, historyTo, roomId]);
  return (
    <section className="pfres-section">
      <div className="pfres-section__header">
        <h2 className="pfres-section__title">Action history</h2>
      </div>
      <div className="pfres-section__body">
        <div className="hk-datebar">
          <div className="hk-history-range">
            <ReportDateRangePicker
              start={historyFrom}
              end={historyTo}
              setStart={setHistoryFrom}
              setEnd={setHistoryTo}
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
              allowLockPeriod
            />
          </div>
          <label>
            Room
            <select
              className="form-select"
              aria-label="Room"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              <option value="">All rooms</option>
              {rooms.map((r) => (
                <option key={r.roomId} value={r.roomId}>
                  {r.roomLabel}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && <p role="alert">{error}</p>}
        <div className="pfres-table-wrap">
          <table className="pfres-table">
            <thead>
              <tr>
                <th>Room / task</th>
                <th>Action</th>
                <th>Occurred</th>
                <th>Recorded by / at</th>
                <th>Staff initials</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td title={e.taskId}>
                    {e.roomLabel} {e.code}
                  </td>
                  <td>
                    {e.type} {e.inspectionResult}
                  </td>
                  <td>{displayInstant(e.occurredAt, timezone)}</td>
                  <td>
                    {e.actorDisplayName}
                    <br />
                    {displayInstant(e.recordedAt, timezone)}
                  </td>
                  <td>{e.staffInitials}</td>
                  <td>{e.reason}</td>
                </tr>
              ))}
            </tbody>
            {!items.length && (
              <tbody>
                <tr>
                  <td
                    colSpan={6}
                    className="pfres-table__empty"
                    role={loading ? "status" : undefined}
                  >
                    {loading ? "Loading history..." : "No recorded actions."}
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
        {cursor && (
          <button
            className="btn btn-outline-contrast"
            disabled={loading}
            onClick={() => load(cursor)}
          >
            Load more
          </button>
        )}
      </div>
    </section>
  );
}
export function Matrix({ data, rooms }: { data: Schedule; rooms: Room[] }) {
  const dates = Array.from({ length: data.days }, (_, i) =>
    shiftDate(data.from, i),
  );
  const groups = [...new Set(rooms.map((r) => r.groupLabel))];
  return (
    <div className="pfres-panel pfres-panel--flush hk-gridwrap">
      <table className="hk-week">
        <thead>
          <tr>
            <th className="hk-pinned">Room</th>
            {dates.map((date) => (
              <th key={date}>{dateLabel(date)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group}>
              <tr className="hk-group">
                <th colSpan={dates.length + 1}>{group}</th>
              </tr>
              {rooms
                .filter((r) => r.groupLabel === group)
                .map((room) => (
                  <tr key={room.roomId}>
                    <th className="hk-pinned">{room.roomLabel}</th>
                    {room.days.map((day) => (
                      <td key={day.date}>
                        {day.tasks
                          .filter((t) => t.state !== "superseded")
                          .map((t) => (
                            <span
                              className="hk-clean-chip"
                              key={t.taskId}
                              style={{
                                backgroundColor: whiteBadgeFill(t.color),
                                color: "#fff",
                              }}
                            >
                              {t.code}
                            </span>
                          ))}
                        {!day.tasks.filter((t) => t.state !== "superseded")
                          .length && (
                          <small className="text-body-secondary">
                            {day.occupancy}
                          </small>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Time</th>
            {data.totals.map((d) => (
              <td key={d.date}>{duration(d.plannedMinutes)}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
export function Workload({ data }: { data: Schedule }) {
  return <WorkloadPivot data={data} />;
}
export function dateLabel(date: string) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
